/**
 * Content Management → Bulk Cinegraphics Upload (Phase 2.68)
 *
 * Mirrors ContentMgmtInfographicsBulk's UX but engineered for HD video
 * payloads (100–200 MB each, often 10+ files at once = 1.5 GB+ per batch).
 *
 * Design choices that are NOT just a copy-paste of the infographics page:
 *
 *   1. **Strictly sequential file processing.** With infographics we still
 *      ran files sequentially but the I/O was tiny. With cinegraphics,
 *      "10 files × 150 MB" is real production load — running them in
 *      parallel would pin the chunked-upload backend and starve the
 *      Cloudflare edge of concurrency budget. So we walk the queue one
 *      video at a time. The chunks WITHIN a single video still upload
 *      in parallel, but throttled to 2 (down from the default 3) so the
 *      proxy + backend stay calm.
 *
 *   2. **Pre-flight per-file size cap = 200 MB.** Anything beyond that we
 *      reject in the browser before a single byte leaves. Matches the
 *      Phase 2.67 cap displayed in Upload Content. The chunked-upload
 *      backend has a 2 GB safety ceiling anyway.
 *
 *   3. **beforeunload warning.** A 1.5 GB batch can take 5–15 minutes
 *      depending on the admin's upstream. We attach a warning so an
 *      accidental tab-close mid-upload doesn't silently kill the run.
 *
 *   4. **Live "X.X / Y.Y MB" progress + smoothed kbps readout.** Numbers
 *      are visible alongside the % so the admin doesn't think the page is
 *      frozen during a long chunk-set. Without this people abandon
 *      uploads thinking they hung.
 *
 *   5. **Defensive auto-classification** — server already classifies by
 *      extension (Phase 2.66) but we ALSO refuse to enqueue files that
 *      don't look like videos. Belt-and-suspenders.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, chunkedUpload } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Video, UploadCloud, CheckCircle2, AlertTriangle, XCircle,
  Loader2, Info, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const ACCEPTED_EXT = /\.(mp4|mov|webm|m4v|mkv|avi)$/i;
const MAX_BYTES = 200 * 1024 * 1024;        // 200 MB per video (Phase 2.67 cap)
const CHUNK_CONCURRENCY = 2;                 // gentler than default 3 — 150 MB × 10 files won't pin the proxy

const BOARD_OPTIONS = [
  { value: 'cbse', label: 'CBSE' },
  { value: 'icse', label: 'ICSE / CISCE' },
  { value: 'ib', label: 'IB' },
  { value: 'igcse', label: 'Cambridge IGCSE' },
  { value: 'state_board', label: 'State Board' },
  { value: 'nios', label: 'NIOS' },
];
const CLASS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1), label: `Class ${i + 1}`,
}));
const SUBJECT_FALLBACK = [
  'Mathematics', 'Science', 'Physics', 'Chemistry', 'Biology', 'Biotechnology',
  'English', 'Hindi', 'Sanskrit',
  'Social Science', 'History', 'Geography', 'Civics', 'Economics',
  'Political Science', 'Psychology', 'Sociology',
  'Accountancy', 'Business Studies', 'Computer Science', 'Informatics Practices',
  'Arts', 'Fine Art', 'Sangeet', 'Home Science', 'Creative Writing & Translation',
  'Physical Education And Well Being', 'Health and Physical Education',
  'Vocational Education', 'Skills Education', 'The World Around Us',
  'EVS/Environmental Studies',
].map(s => ({ value: s, label: s }));

const STAGE = {
  PICKED: 'picked',
  CHECKING: 'checking-duplicate',
  DUPLICATE: 'duplicate',
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  READY: 'ready',
  INGESTING: 'ingesting',
  INGESTED: 'ingested',
  FAILED: 'failed',
};

function stageBadge(stage, pct) {
  switch (stage) {
    case STAGE.PICKED: return <Badge className="bg-slate-100 text-slate-600 rounded-full text-[10px]">picked</Badge>;
    case STAGE.CHECKING: return <Badge className="bg-slate-100 text-slate-500 rounded-full text-[10px]">checking…</Badge>;
    case STAGE.DUPLICATE: return <Badge className="bg-rose-100 text-rose-700 rounded-full text-[10px] uppercase tracking-wider">Duplicate</Badge>;
    case STAGE.UPLOADING: return <Badge className="bg-amber-100 text-amber-700 rounded-full text-[10px]" data-testid="cm-cine-uploading">uploading… {typeof pct === 'number' && pct > 0 ? `${pct}%` : ''}</Badge>;
    case STAGE.UPLOADED: return <Badge className="bg-sky-100 text-sky-700 rounded-full text-[10px]">uploaded</Badge>;
    case STAGE.READY: return <Badge className="bg-indigo-100 text-indigo-700 rounded-full text-[10px]">ready</Badge>;
    case STAGE.INGESTING: return <Badge className="bg-indigo-100 text-indigo-700 rounded-full text-[10px]">ingesting…</Badge>;
    case STAGE.INGESTED: return <Badge className="bg-emerald-100 text-emerald-700 rounded-full text-[10px] uppercase tracking-wider">Pending review</Badge>;
    case STAGE.FAILED: return <Badge className="bg-rose-100 text-rose-700 rounded-full text-[10px]">failed</Badge>;
    default: return null;
  }
}

function fmtMb(bytes) {
  return (bytes / 1024 / 1024).toFixed(1);
}


/**
 * Generate a JPEG data-URL thumbnail for a local video file, entirely in
 * the browser. Loads only metadata + the first ~1s of video so big files
 * don't blow memory. Returns null on any failure (codec the browser can't
 * decode, corrupt file, etc.) — the row falls back to the generic video
 * icon so we never block the upload pipeline on a missing preview.
 *
 * Why this matters: when staff drops 10 clips with near-identical filenames
 * (e.g. "CBSE-Class-12-Mathematics-Chapter-3.mp4"), a 1-frame thumbnail
 * makes "did I pick the right clip?" a glance, not a guess.
 */
async function generateVideoThumbnail(file) {
  return new Promise((resolve) => {
    let url;
    let done = false;
    const finish = (val) => { if (done) return; done = true; if (url) try { URL.revokeObjectURL(url); } catch { /* ignore */ } resolve(val); };
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      url = URL.createObjectURL(file);
      video.src = url;
      // Bail after 6 seconds — some codecs need a fallback so we don't
      // hang the row in "loading thumbnail" forever.
      const watchdog = setTimeout(() => finish(null), 6000);
      video.addEventListener('loadedmetadata', () => {
        // Seek to ~1 second (or 1/4 of the way in for very short clips).
        // Avoids the typical black opening frame of educational videos.
        const target = Math.min(1, Math.max(0, (video.duration || 4) / 4));
        try { video.currentTime = target; } catch { finish(null); }
      });
      video.addEventListener('seeked', () => {
        try {
          const w = Math.min(160, video.videoWidth || 160);
          const h = video.videoWidth
            ? Math.max(1, Math.round((w / video.videoWidth) * (video.videoHeight || 90)))
            : 90;
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, w, h);
          // 0.6 quality JPEG keeps each thumb under ~10 KB even at HD source
          // — a batch of 50 rows is well under 1 MB of state.
          const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
          clearTimeout(watchdog);
          finish(dataUrl);
        } catch {
          clearTimeout(watchdog);
          finish(null);
        }
      });
      video.addEventListener('error', () => { clearTimeout(watchdog); finish(null); });
    } catch {
      finish(null);
    }
  });
}

export default function ContentMgmtCinegraphicsBulk({ onBatchIngested }) {
  const inputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [subjectCache, setSubjectCache] = useState({});

  const subjectCacheKey = (b, c) => (b && c ? `${b}:${c}` : '');

  const loadSubjectsFor = async (b, c) => {
    const key = subjectCacheKey(b, c);
    if (!key || subjectCache[key]) return;
    try {
      const r = await apiGet(`/onboarding/subjects?board=${encodeURIComponent(b)}&class_level=${encodeURIComponent(c)}`);
      const arr = (r.subjects || [])
        .map(s => (typeof s === 'string' ? s : s.name))
        .filter(Boolean)
        .map(s => ({ value: s, label: s }));
      setSubjectCache(prev => ({ ...prev, [key]: arr.length ? arr : SUBJECT_FALLBACK }));
    } catch {
      setSubjectCache(prev => ({ ...prev, [key]: SUBJECT_FALLBACK }));
    }
  };

  const subjectsFor = (b, c) => {
    const key = subjectCacheKey(b, c);
    if (!key) return SUBJECT_FALLBACK;
    return subjectCache[key] || SUBJECT_FALLBACK;
  };

  const pick = () => inputRef.current?.click();
  const setRow = (id, patch) =>
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

  const handleFiles = async (files) => {
    const all = Array.from(files);
    // Two filters: (1) must look like a video by mime OR extension; (2)
    // must be ≤ 200 MB. We report each rejected file by name so the
    // admin knows which clip to re-encode / replace.
    const ok = [], badType = [], tooBig = [];
    for (const f of all) {
      const looksVideo = f.type.startsWith('video/') || ACCEPTED_EXT.test(f.name);
      if (!looksVideo) { badType.push(f.name); continue; }
      if (f.size > MAX_BYTES) { tooBig.push(`${f.name} (${fmtMb(f.size)} MB)`); continue; }
      ok.push(f);
    }
    if (badType.length) toast.error(`Not a video: ${badType.slice(0, 3).join(', ')}${badType.length > 3 ? ` (+${badType.length - 3} more)` : ''}`);
    if (tooBig.length) toast.error(`Over 200 MB: ${tooBig.slice(0, 2).join(', ')}${tooBig.length > 2 ? ` (+${tooBig.length - 2} more)` : ''}. Re-encode at lower bitrate first.`);
    if (!ok.length) return;

    const now = Date.now();
    const newRows = ok.map((f, i) => ({
      id: `cr_${now}_${i}_${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      name: f.name,
      sizeMb: +(f.size / 1024 / 1024).toFixed(2),
      stage: STAGE.CHECKING,
      error: null,
      fileUrl: null,
      duplicate: false,
      dupExisting: null,
      detected: null,
      board: '',
      classLevel: '',
      subject: '',
      topic: '',
      heading: '',
      contentId: null,
      uploadPct: 0,
      bytesUploaded: 0,
      thumbnailUrl: null,    // populated async by generateVideoThumbnail
      thumbnailLoading: true,
    }));
    setRows(prev => [...prev, ...newRows]);

    // Fire all thumbnail generators in parallel — they don't block dup
    // check or classify, and each row patches itself as it completes.
    // Failures silently fall back to the video icon.
    newRows.forEach(async (r) => {
      const thumb = await generateVideoThumbnail(r.file);
      setRow(r.id, { thumbnailUrl: thumb, thumbnailLoading: false });
    });

    try {
      const [dupRes, classifyRes] = await Promise.all([
        apiPost('/content/check-duplicates', {
          filenames: newRows.map(r => r.name),
          content_type: 'cinegraphic',
        }),
        apiPost('/content/bulk-classify', {
          filenames: newRows.map(r => r.name),
          content_type: 'cinegraphic',
        }),
      ]);
      const dupMap = dupRes.duplicates || {};
      const clsByName = {};
      (classifyRes.items || []).forEach(it => { clsByName[it.filename] = it.detected || {}; });

      setRows(prev => prev.map(r => {
        if (!newRows.some(n => n.id === r.id)) return r;
        const dup = dupMap[r.name];
        const detected = clsByName[r.name] || {};
        if (dup) {
          return { ...r, stage: STAGE.DUPLICATE, duplicate: true, dupExisting: dup, detected };
        }
        return {
          ...r, stage: STAGE.PICKED, detected,
          board: detected.board || '',
          classLevel: detected.class_level || '',
          subject: detected.subject || '',
          topic: detected.topic || '',
        };
      }));
      const dupCount = Object.keys(dupMap).length;
      if (dupCount > 0) {
        toast.error(`${dupCount} video${dupCount === 1 ? '' : 's'} already in the library — those will be skipped`);
      }
    } catch {
      // Classifier failure must NOT block the admin — keep the rows and
      // let them fill the metadata manually.
      setRows(prev => prev.map(r =>
        newRows.some(n => n.id === r.id) && r.stage === STAGE.CHECKING
          ? { ...r, stage: STAGE.PICKED } : r
      ));
    }
  };

  const onPick = (e) => handleFiles(e.target.files);
  const onDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

  // Bulk-apply scope to all editable rows (most admins drop 10 clips for
  // the SAME chapter — saves them clicking through every dropdown).
  const [bulkBoard, setBulkBoard] = useState('');
  const [bulkClass, setBulkClass] = useState('');
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkTopic, setBulkTopic] = useState('');

  useEffect(() => {
    if (bulkBoard && bulkClass) loadSubjectsFor(bulkBoard, bulkClass);
  }, [bulkBoard, bulkClass]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const combos = new Set();
    rows.forEach(r => { if (r.board && r.classLevel) combos.add(`${r.board}:${r.classLevel}`); });
    combos.forEach(c => {
      const [b, cl] = c.split(':');
      loadSubjectsFor(b, cl);
    });
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyBulkToReady = () => {
    if (!bulkBoard && !bulkClass && !bulkSubject && !bulkTopic) {
      toast.error('Pick at least one field to apply');
      return;
    }
    setRows(prev => prev.map(r => {
      if (r.stage !== STAGE.PICKED && r.stage !== STAGE.READY && r.stage !== STAGE.UPLOADED) return r;
      return {
        ...r,
        board: bulkBoard || r.board,
        classLevel: bulkClass || r.classLevel,
        subject: bulkSubject || r.subject,
        topic: bulkTopic || r.topic,
      };
    }));
    toast.success('Applied to all editable rows');
  };

  // beforeunload guard — videos take a while to upload; protect against
  // accidental tab close mid-batch. Only attached while at least one row
  // is actively uploading or ingesting.
  useEffect(() => {
    const busy = rows.some(r => r.stage === STAGE.UPLOADING || r.stage === STAGE.INGESTING);
    if (!busy) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = 'Cinegraphic upload still running — leaving will abort it. Continue?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [rows]);

  // Upload ONE video, chunked. Throttled chunk concurrency = 2 so a 1.5 GB
  // batch doesn't smother the proxy. Live progress + bytes-uploaded readout.
  const uploadOne = async (row) => {
    if (row.duplicate) return;
    try {
      setRow(row.id, { stage: STAGE.UPLOADING, error: null, uploadPct: 0, bytesUploaded: 0 });
      const up = await chunkedUpload(row.file, {
        concurrency: CHUNK_CONCURRENCY,
        onProgress: (loaded, total) => {
          const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
          setRow(row.id, { uploadPct: pct, bytesUploaded: loaded });
        },
      });
      setRow(row.id, { stage: STAGE.READY, fileUrl: up.file_url, uploadPct: 100, bytesUploaded: row.file.size });
    } catch (ex) {
      setRow(row.id, { stage: STAGE.FAILED, error: ex?.message || 'upload failed' });
    }
  };

  // Sequential — one video at a time. Critical for HD video batches.
  const uploadAllPending = async () => {
    const pending = rows.filter(r => r.stage === STAGE.PICKED && !r.duplicate);
    if (!pending.length) { toast.error('No new videos to upload'); return; }
    setRunning(true);
    for (const r of pending) {
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(r);
    }
    setRunning(false);
    const ok = pending.filter(p => rows.find(r => r.id === p.id)?.stage !== STAGE.FAILED).length;
    if (ok > 0) toast.success(`Uploaded ${ok}/${pending.length} videos — review fields then Submit for Review.`);
  };

  const ingestOne = async (row) => {
    if (!row.board || !row.classLevel || !row.subject) {
      setRow(row.id, { stage: STAGE.FAILED, error: 'board / class / subject required' });
      return { status: 'error' };
    }
    setRow(row.id, { stage: STAGE.INGESTING, error: null });
    try {
      const res = await apiPost('/content/bulk-ingest', {
        items: [{
          file_url: row.fileUrl,
          content_type: 'cinegraphic',
          board: row.board,
          class_level: row.classLevel,
          subject: row.subject,
          topic: row.topic || '',
          heading: row.heading || '',
          title: row.detected?.topic || row.topic || row.name.replace(/\.[^.]+$/, ''),
          original_filename: row.name,
        }],
      });
      const out = (res.outcomes || [])[0] || {};
      if (out.status === 'ok') {
        setRow(row.id, { stage: STAGE.INGESTED, contentId: out.content_id });
        return { status: 'ok' };
      }
      setRow(row.id, { stage: STAGE.FAILED, error: out.error || 'ingest failed' });
      return { status: 'error' };
    } catch (ex) {
      setRow(row.id, { stage: STAGE.FAILED, error: ex?.message || 'network error' });
      return { status: 'error' };
    }
  };

  const ingestAllReady = async () => {
    const ready = rows.filter(r => r.stage === STAGE.READY);
    if (!ready.length) { toast.error('No ready rows to submit'); return; }
    setRunning(true);
    let ok = 0, err = 0;
    for (const r of ready) {
      // eslint-disable-next-line no-await-in-loop
      const out = await ingestOne(r);
      if (out.status === 'ok') ok++; else err++;
    }
    setRunning(false);
    if (err === 0) toast.success(`All ${ok} cinegraphics submitted for admin review`);
    else if (ok === 0) toast.error(`Submission failed for all ${err} videos`);
    else toast.warning(`Submitted ${ok}/${ok + err} — ${err} failed`);
    if (ok > 0 && onBatchIngested) onBatchIngested();
  };

  const retryRow = async (row) => {
    if (row.duplicate) return 'skip';
    if (!row.fileUrl) {
      await uploadOne(row);
      return 'ok-partial';
    }
    const out = await ingestOne(row);
    return out.status === 'ok' ? 'ok' : 'error';
  };

  const retryAllFailed = async () => {
    const failed = rows.filter(r => r.stage === STAGE.FAILED);
    if (!failed.length) { toast.error('No failed rows to retry'); return; }
    setRunning(true);
    let ok = 0, partial = 0, err = 0;
    for (const r of failed) {
      // eslint-disable-next-line no-await-in-loop
      const result = await retryRow(r);
      if (result === 'ok') ok++;
      else if (result === 'ok-partial') partial++;
      else if (result === 'error') err++;
    }
    setRunning(false);
    const total = ok + partial + err;
    if (err === 0 && partial === 0) {
      toast.success(`Retried ${ok} video(s) successfully`);
      if (ok > 0 && onBatchIngested) onBatchIngested();
    } else if (ok + partial === 0) {
      toast.error(`All ${total} retry attempt(s) failed`);
    } else {
      const parts = [];
      if (ok > 0) parts.push(`${ok} submitted`);
      if (partial > 0) parts.push(`${partial} re-uploaded (click Submit to ingest)`);
      if (err > 0) parts.push(`${err} still failing`);
      toast.warning(`Retried ${total}: ${parts.join(' · ')}`);
      if (ok > 0 && onBatchIngested) onBatchIngested();
    }
  };

  const counts = useMemo(() => ({
    total: rows.length,
    totalMb: +rows.reduce((a, r) => a + r.sizeMb, 0).toFixed(1),
    dup: rows.filter(r => r.duplicate).length,
    pending: rows.filter(r => r.stage === STAGE.PICKED && !r.duplicate).length,
    ready: rows.filter(r => r.stage === STAGE.READY).length,
    ingested: rows.filter(r => r.stage === STAGE.INGESTED).length,
    failed: rows.filter(r => r.stage === STAGE.FAILED).length,
  }), [rows]);

  return (
    <div className="space-y-5" data-testid="cm-cinegraphics-bulk">
      <Card className="p-5 rounded-2xl border-slate-100">
        <div className="flex items-center gap-2 mb-1">
          <Video className="w-4 h-4 text-rose-500" />
          <h3 className="font-heading text-sm font-semibold text-slate-800">Bulk upload cinegraphics</h3>
        </div>
        <p className="text-[11px] text-slate-500 font-body mb-4">
          Drop one or more video files (MP4 / WebM / MOV). Filenames following <code className="px-1 py-0.5 rounded bg-slate-100 text-[10px]">Board-Class-Subject-Chapter.mp4</code> are auto-classified. Videos upload <strong>one at a time</strong>, each split into 5 MB chunks with 3-retry backoff so an unstable connection won&apos;t kill the batch. Each video must be &le; 200 MB. Submissions land in <strong>Pending Review</strong> for admin approval in the Content Library.
        </p>

        <div
          onDragOver={e => e.preventDefault()}
          onDrop={onDrop}
          onClick={pick}
          className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-8 text-center cursor-pointer hover:bg-slate-50"
          data-testid="cm-cinegraphics-dropzone">
          <UploadCloud className="w-9 h-9 mx-auto text-slate-400 mb-2" />
          <p className="font-heading text-sm text-slate-700">Drop videos here or click to choose files</p>
          <p className="text-[10px] text-slate-400 font-body mt-1">Accepts .mp4, .mov, .webm, .m4v, .mkv, .avi · max 200 MB each</p>
          <input
            ref={inputRef} type="file" accept="video/*,.mp4,.mov,.webm,.m4v,.mkv,.avi" multiple
            onChange={onPick} className="hidden"
            data-testid="cm-cinegraphics-file-input"
          />
        </div>

        {rows.length > 0 && (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-[10px] font-heading uppercase tracking-wider">
              <Badge className="bg-slate-100 text-slate-700 rounded-full">Total: {counts.total} · {counts.totalMb} MB</Badge>
              {counts.dup > 0 && <Badge className="bg-rose-100 text-rose-700 rounded-full">Duplicates: {counts.dup}</Badge>}
              {counts.pending > 0 && <Badge className="bg-amber-100 text-amber-700 rounded-full">To upload: {counts.pending}</Badge>}
              {counts.ready > 0 && <Badge className="bg-indigo-100 text-indigo-700 rounded-full">Ready: {counts.ready}</Badge>}
              {counts.ingested > 0 && <Badge className="bg-emerald-100 text-emerald-700 rounded-full">Pending review: {counts.ingested}</Badge>}
              {counts.failed > 0 && <Badge className="bg-rose-100 text-rose-700 rounded-full">Failed: {counts.failed}</Badge>}
            </div>

            {/* Bulk-apply scope */}
            <div className="mt-4 rounded-xl bg-slate-50/70 border border-slate-100 p-3">
              <p className="text-[10px] font-heading uppercase tracking-wider text-slate-500 mb-2">Bulk-apply scope to all editable rows</p>
              <div className="flex flex-wrap items-center gap-2">
                <select value={bulkBoard} onChange={e => setBulkBoard(e.target.value)}
                  data-testid="cm-cinegraphics-bulk-board"
                  className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1.5">
                  <option value="">Board…</option>
                  {BOARD_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
                <select value={bulkClass} onChange={e => setBulkClass(e.target.value)}
                  data-testid="cm-cinegraphics-bulk-class"
                  className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1.5">
                  <option value="">Class…</option>
                  {CLASS_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <select value={bulkSubject} onChange={e => setBulkSubject(e.target.value)}
                  data-testid="cm-cinegraphics-bulk-subject"
                  disabled={!bulkBoard || !bulkClass}
                  title={(!bulkBoard || !bulkClass) ? 'Pick Board + Class first to filter subjects' : ''}
                  className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1.5 disabled:bg-slate-50 disabled:text-slate-400">
                  <option value="">{(!bulkBoard || !bulkClass) ? 'Pick Board+Class first…' : 'Subject…'}</option>
                  {subjectsFor(bulkBoard, bulkClass).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <input type="text" value={bulkTopic} onChange={e => setBulkTopic(e.target.value)}
                  placeholder="Chapter / topic (optional)"
                  data-testid="cm-cinegraphics-bulk-topic"
                  className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1.5 flex-1 min-w-[180px]"
                />
                <Button onClick={applyBulkToReady} variant="ghost"
                  className="text-xs font-heading text-rose-600 hover:bg-rose-50"
                  data-testid="cm-cinegraphics-bulk-apply">Apply to all</Button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={uploadAllPending} disabled={counts.pending === 0 || running}
                data-testid="cm-cinegraphics-upload-all-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full text-xs font-heading disabled:opacity-40">
                Upload &amp; auto-classify ({counts.pending})
              </Button>
              <Button onClick={ingestAllReady} disabled={counts.ready === 0 || running}
                data-testid="cm-cinegraphics-ingest-all-btn"
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-xs font-heading disabled:opacity-40">
                {running ? 'Submitting…' : `Submit ${counts.ready} for review`}
              </Button>
              {counts.failed > 0 && (
                <Button onClick={retryAllFailed} disabled={running}
                  data-testid="cm-cinegraphics-retry-all-btn"
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs font-heading disabled:opacity-40">
                  <RotateCcw className="w-3 h-3 mr-1.5" />
                  Retry {counts.failed} failed
                </Button>
              )}
              <Button onClick={() => setRows([])} disabled={running} variant="ghost"
                className="text-slate-500 text-xs font-heading disabled:opacity-40"
                data-testid="cm-cinegraphics-clear-btn">Clear list</Button>
            </div>
          </>
        )}
      </Card>

      {rows.length > 0 && (
        <Card className="p-0 rounded-2xl border-slate-100 overflow-hidden" data-testid="cm-cinegraphics-rows">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50/70 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Filename</th>
                  <th className="text-left px-3 py-2">Board</th>
                  <th className="text-left px-3 py-2">Class</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-left px-3 py-2">Chapter</th>
                  <th className="text-center px-3 py-2">Stage</th>
                  <th className="text-center px-3 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const locked = r.stage === STAGE.UPLOADING
                    || r.stage === STAGE.INGESTING
                    || r.stage === STAGE.INGESTED
                    || r.stage === STAGE.DUPLICATE
                    || r.stage === STAGE.CHECKING;
                  return (
                    <tr key={r.id}
                      className={`border-t border-slate-100 ${r.duplicate ? 'bg-rose-50/50' : r.stage === STAGE.FAILED ? 'bg-rose-50/30' : r.stage === STAGE.INGESTED ? 'bg-emerald-50/30' : ''}`}
                      data-testid={`cm-cine-row-${idx}`}>
                      <td className="px-3 py-2 max-w-[320px]">
                        <div className="flex items-start gap-2.5">
                          {/* Thumbnail — first non-black frame at ~1s in.
                              Falls back to the video icon if the codec
                              isn't browser-decodable or thumbnailing
                              hasn't finished yet. */}
                          <div
                            className="flex-shrink-0 w-16 h-10 rounded-md overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center relative"
                            data-testid={`cm-cine-row-thumb-${idx}`}>
                            {r.thumbnailUrl ? (
                              <img src={r.thumbnailUrl} alt=""
                                className="w-full h-full object-cover"
                                draggable={false} />
                            ) : r.thumbnailLoading ? (
                              <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />
                            ) : (
                              <Video className="w-4 h-4 text-slate-400" />
                            )}
                            {/* Tiny play glyph overlay so it always reads as "video" */}
                            {r.thumbnailUrl && (
                              <span className="absolute bottom-0 right-0 bg-slate-900/70 rounded-tl-md px-0.5 py-px">
                                <Video className="w-2.5 h-2.5 text-white" />
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-body text-slate-800 break-all leading-snug" title={r.name}>{r.name}</p>
                            <p className="text-[10px] text-slate-400 font-body mt-0.5">
                              {r.sizeMb} MB
                              {r.stage === STAGE.UPLOADING && r.bytesUploaded > 0 && (
                                <span className="text-amber-600 ml-1">
                                  · {fmtMb(r.bytesUploaded)} / {r.sizeMb} MB
                                </span>
                              )}
                            </p>
                            {r.duplicate && r.dupExisting && (
                              <p className="text-[10px] text-rose-600 font-body mt-1">
                                Uploaded on {r.dupExisting.created_at?.slice(0, 10) || '—'}
                                {r.dupExisting.topic ? ` · ${r.dupExisting.topic}` : ''}
                              </p>
                            )}
                            {r.error && (
                              <p className="text-[10px] text-rose-600 font-body mt-1" title={r.error}>
                                <AlertTriangle className="inline w-3 h-3 mr-0.5" />{r.error}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 min-w-[110px]">
                        <select disabled={locked} value={r.board}
                          onChange={e => setRow(r.id, { board: e.target.value })}
                          data-testid={`cm-cine-row-board-${idx}`}
                          className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50">
                          <option value="">—</option>
                          {BOARD_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 min-w-[90px]">
                        <select disabled={locked} value={r.classLevel}
                          onChange={e => setRow(r.id, { classLevel: e.target.value })}
                          data-testid={`cm-cine-row-class-${idx}`}
                          className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50">
                          <option value="">—</option>
                          {CLASS_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 min-w-[140px]">
                        <select disabled={locked || !r.board || !r.classLevel} value={r.subject}
                          onChange={e => setRow(r.id, { subject: e.target.value })}
                          data-testid={`cm-cine-row-subject-${idx}`}
                          title={(!r.board || !r.classLevel) ? 'Pick Board + Class first' : ''}
                          className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50 disabled:text-slate-400">
                          <option value="">—</option>
                          {subjectsFor(r.board, r.classLevel).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 min-w-[160px]">
                        <input disabled={locked} type="text" value={r.topic}
                          onChange={e => setRow(r.id, { topic: e.target.value })}
                          placeholder="chapter / topic"
                          data-testid={`cm-cine-row-topic-${idx}`}
                          className="w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1 disabled:bg-slate-50" />
                      </td>
                      <td className="px-3 py-2 text-center" data-testid={`cm-cine-row-stage-${idx}`}>
                        <div className="flex items-center justify-center gap-1.5">
                          {r.stage === STAGE.INGESTED && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                          {r.stage === STAGE.FAILED && <AlertTriangle className="w-4 h-4 text-rose-500" />}
                          {(r.stage === STAGE.UPLOADING || r.stage === STAGE.INGESTING || r.stage === STAGE.CHECKING) && (
                            <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
                          )}
                          {stageBadge(r.stage, r.uploadPct)}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {r.stage === STAGE.FAILED && (
                            <button onClick={() => retryRow(r)} disabled={running}
                              className="text-amber-500 hover:text-amber-600 disabled:opacity-40"
                              title="Retry this video"
                              data-testid={`cm-cine-row-retry-${idx}`}>
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => removeRow(r.id)} disabled={running}
                            className="text-slate-400 hover:text-rose-500"
                            title="Remove from list"
                            data-testid={`cm-cine-row-remove-${idx}`}>
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-slate-50/60 border-t border-slate-100 flex items-center gap-2 text-[10px] text-slate-500 font-body">
            <Info className="w-3 h-3" />
            Videos upload sequentially (one file at a time) and chunked (5 MB pieces with 3-retry backoff). Submissions appear under Content Library → Pending Review for approval.
          </div>
        </Card>
      )}
    </div>
  );
}
